import * as Contract from '@tableau/api-internal-contract-js';
import { CommandMessage, DialogStyle, Messenger } from '@tableau/api-internal-contract-js';
import * as PresLayerContract from '@tableau/preslayer-api-contract';
import { AddInInstancePresModel, ErrorHelpers } from '../ApiCore';
import { ApiDispatcher } from '../common-bootstrap/ApiDispatcher';
import { ExtensionUtils } from '../common-bootstrap/ExtensionUtils';
import { UINamespaceHandler, UINamespaceOptions } from '../common-bootstrap/UINamespaceHandler';
import { PresLayerOptionalCompatibility } from '../mappings/PresLayerOptionalCompatibility';
import { telemetryFunctionComplete } from '../telemetry/ExtensionsTelemetryDispatcher';
import { FunctionResults, telemetryFunctionStart } from '../telemetry/TelemetryDispatcher';
import { Dialog } from './Dialog';
import { DialogElement } from './DialogElement';
import { DialogWindow } from './DialogWindow';

/**
 * This class is a wrapper for the handling of UINamespace commands (displayDialog
 * and closeDialog) on the server.
 */
export class ServerUINamespaceHandler implements UINamespaceHandler {
  // This member variable keeps track of the active dialog instance so it
  // can be ensure that an extension never opens more than one at a time.
  private _activeDialog?: Dialog;

  /**
   * Creates an instance of ServerUINamespaceHandler.
   * @param presLayer The instantiated pres layer for this class to use
   * @param extensionLocator Locator with info about the extension
   * @param _messenger An instantiated Messenger object for us to use
   * @param _closeNotificationCallback The function to be called when a tableau.extensions.closeDialog()
   *  is called by a developer.  This callback will close the dialog and notify parent of closure.
   *
   */
  public constructor(
    private _presLayer: PresLayerContract.PresentationLayer,
    private _extensionInstance: AddInInstancePresModel,
    private _loadedExtensionUrl: string,
    private _messenger: Messenger,
    private _closeNotificationCallback?: (closePayload: string) => void,
  ) {}

  /**
   * If the passed command is one that is handled by this handler (i.e. related to the
   * UINamespace), this will handle that command and return true.  Returns false otherwise.
   */
  public tryHandleCommand(msg: CommandMessage): boolean {
    if (msg.verbId === Contract.VerbId.DisplayDialog) {
      const runtimeData = telemetryFunctionStart();
      this.handleDisplayDialogCommand(msg);
      telemetryFunctionComplete(msg.verbId, runtimeData, msg.parameters, FunctionResults.Success({}), this._extensionInstance);
      return true;
    } else if (msg.verbId === Contract.VerbId.CloseDialog) {
      const runtimeData = telemetryFunctionStart();
      this.handleCloseDialogCommand(msg);
      telemetryFunctionComplete(msg.verbId, runtimeData, msg.parameters, FunctionResults.Success({}), this._extensionInstance);
      return true;
    } else {
      return false;
    }
  }

  /**
   * Sets up a new APIDispatcher instance and opens a new dialog window to the URL
   * passed as a parameter to msg.  The pairing of this APIDispatcher and the new window
   * allows the extension running inside the dialog to communicate with the server
   * platform code and access extension APIs.
   *
   * @param msg the message sent by the parent extension via displayDialogAsync
   */
  public handleDisplayDialogCommand(msg: CommandMessage): void {
    const url = msg.parameters[Contract.ParameterId.ExtensionDialogUrl] as string;
    const addInRegistrationPresModel = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(
      this._extensionInstance.addInRegistrationPresModel,
    );
    ErrorHelpers.verifyValue(url, 'url');
    ErrorHelpers.verifyValue(addInRegistrationPresModel.url, 'this._extensionInstance.addInRegistrationPresModel.url');
    ErrorHelpers.verifyValue(this._loadedExtensionUrl, 'this._loadedExtensionUrl');

    // Decoder key to the four urls used below:
    // url = The requested dialog url. It could be relative or absolute.
    // --> './dialog.html'
    // --> 'https://host/extension-name/dialog.html'
    // dialogUrl = The resulting absolute url after combining url with the base of loadedExtensionUrl.
    // --> 'https://host/extension-name/dialog.html'
    // --> 'http://qa-near/vizql/v_base/extensions/extension-name/dialog.html' # first class extension
    // extensionUrl = The original extension url from the manifest that has been stored in addInRegistrationPresModel.
    // --> 'https://host/extension-name/index.html'
    // --> 'tableau:/extensions/extension-name/index.html'    # first class extension
    // loadedExtensionUrl = The original extension url transformed into a valid scheme and loaded.
    // --> 'https://host/extension-name/index.html'
    // --> 'http://qa-near/vizql/v_base/extensions/extension-name/index.html' # first class extension
    // (loadedExtensionUrl comes from ApiDispatcher.setOverrideMessageTarget)
    const extensionUrl: URL = new URL(addInRegistrationPresModel.url);
    const loadedExtensionUrl: URL = new URL(this._loadedExtensionUrl);
    const dialogUrl: URL = ExtensionUtils.normalizeExtensionDialogUrl(loadedExtensionUrl, url);
    const isValid: boolean = ExtensionUtils.isValidDialogUrl(extensionUrl, loadedExtensionUrl, url, dialogUrl);
    const dialogStyle: string = msg.parameters[Contract.ParameterId.ExtensionDialogStyle] as DialogStyle;

    if (!isValid) {
      this._messenger.prepareCommandResponseMessage(msg.msgGuid, Contract.ExtensionDialogResult.InvalidDomain, undefined).send();

      // Next, check if there is already an active window for this extension.  An extension
      // is only allowed a single dialog at a time, if not, send back the correct error code.
    } else if (this._activeDialog && this._activeDialog.isOpen) {
      this._messenger.prepareCommandResponseMessage(msg.msgGuid, Contract.ExtensionDialogResult.DialogAlreadyOpen, undefined).send();
    } else {
      // After the checks have passed, set up our url to be the normalized dialogUrl,
      // and create the APIDispatcher that will allow for
      // communication between the window that is about to be opened and the Tableau Server platform.
      const initialPayload = msg.parameters[Contract.ParameterId.ExtensionDialogPayload] as string;

      // The new APIDispatcher can reuse most of the same extension instance info,
      // but it has a new URL and that must be customized.
      const extensionInstanceCopy = Object.assign({}, this._extensionInstance);
      extensionInstanceCopy.addInRegistrationPresModel = Object.assign({}, this._extensionInstance.addInRegistrationPresModel);
      extensionInstanceCopy.addInRegistrationPresModel.url = dialogUrl.toString();

      const callback = this.handleExpectedCloseEvent.bind(this);
      const options: UINamespaceOptions = {
        initialPayload: initialPayload,
        closeNotificationCallback: callback,
      };

      const dialogDispatcher: ApiDispatcher = new ApiDispatcher(extensionInstanceCopy, this._presLayer, window, options);

      const w = msg.parameters[Contract.ParameterId.ExtensionDialogW] as string;
      const h = msg.parameters[Contract.ParameterId.ExtensionDialogH] as string;

      // Setting the specific dialog based on the style
      if (dialogStyle === DialogStyle.Modal) {
        this._activeDialog = new DialogElement(w, h, dialogUrl.toString(), true, this._extensionInstance, () => {
          this.handleUserCloseEvent();
        });
      } else if (dialogStyle === DialogStyle.Modeless) {
        this._activeDialog = new DialogElement(w, h, dialogUrl.toString(), false, this._extensionInstance, () => {
          this.handleUserCloseEvent();
        });
      } else {
        this._activeDialog = new DialogWindow(w, h, dialogUrl.toString(), () => {
          this.handleUserCloseEvent();
        });
      }

      this._activeDialog.createDialog().then(() => {
        // Check if the activeDialog was created successfully.
        // In this case an error has been logged and we exit.
        if (!this._activeDialog!.isCreated) {
          return;
        }

        // Finally, setup the new dispatcher with the newly created dialog.
        this._activeDialog!.showDialog(dialogDispatcher);
        this._messenger.prepareCommandResponseMessage(msg.msgGuid, Contract.ExtensionDialogResult.Success, undefined).send();
      });
    }
  }

  /**
   * Given a close dialog message, this executes the stored close callback of the parent instance.
   */
  public handleCloseDialogCommand(msg: CommandMessage): void {
    if (this._closeNotificationCallback) {
      const closePayload = msg.parameters[Contract.ParameterId.ExtensionDialogPayload] as string;
      this._closeNotificationCallback(closePayload);
    }
  }

  /**
   * Sends a notification to the parent extension that the dialog has been closed as expected,
   * via extensions.ui.closeDialog, along with the close payload from the dialog extension.
   *
   * In the UINamespace context, this method is the callback that is passed from the parent to the
   * dialog extension.  It will be executed on the parent side once the closeDialog command has been run
   * from the dialog side.  Thus, this method also is responsible for closing the child dialog.  The notification
   * it sends will travel from the parent side preslayer to the parent side client layer (the developer code).
   *
   * @param closePayload
   */
  private handleExpectedCloseEvent(closePayload?: string): void {
    this._activeDialog?.closeDialog();

    const dialogEvent: Contract.DialogUpdateEvent = {
      isCloseEvent: true,
      closePayload: closePayload,
    };

    this._messenger.prepareNotificationMessage(Contract.NotificationId.ExtensionDialogUpdate, dialogEvent).send();
  }

  /**
   * Sends a notification to the parent extension that the dialog has been closed in an unexpected way,
   * typically by the user exiting out of the dialog themselves.
   */
  private handleUserCloseEvent(): void {
    this._activeDialog?.cleanupDialog();
    const dialogEvent: Contract.DialogUpdateEvent = {
      isCloseEvent: false,
    };

    this._messenger.prepareNotificationMessage(Contract.NotificationId.ExtensionDialogUpdate, dialogEvent).send();
  }
}
